/**
 * DRBFM History Page
 *
 * Displays a list of past DRBFM analysis sessions with ability to:
 * - View session details and results
 * - Export results
 * - Delete sessions
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  PlaylistAdd as ConvertIcon,
  ThreeDRotation as ThreeDIcon,
} from '@mui/icons-material';
import { useDRBFMSessions } from '@hooks/useDRBFM';
import { drbfmService } from '@services/drbfm.service';
import { DRBFMSession, SessionResultsResponse } from '../types';
import { ConvertToVerificationDialog } from '@components/drbfm';

const DRBFMHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { sessions, isLoading, error, fetchSessions, deleteSession, clearError } = useDRBFMSessions();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<DRBFMSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // HTML Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewSession, setPreviewSession] = useState<DRBFMSession | null>(null);
  const [previewHTML, setPreviewHTML] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Convert to verification state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertSession, setConvertSession] = useState<DRBFMSession | null>(null);
  const [convertResults, setConvertResults] = useState<SessionResultsResponse | null>(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/drbfm');
  };

  // Handle view session
  const handleViewSession = async (session: DRBFMSession) => {
    // Navigate to DRBFM page with session ID to load results
    navigate(`/drbfm?session=${session.id}`);
  };

  // Handle 3D viewer navigation. cad_model_id is not on the session row — it
  // lives on each failure_mode's cad_anchor — so fetch the results, pull the
  // first anchored cad_model_id, and pass both to the viewer. Without
  // cad_model in the URL, the viewer's marker-filter (`selectedModel.id ===
  // anchor.cadModelId`) returns an empty set and the demo looks broken (TG
  // #673). Fall back to session-only if no anchors are present yet.
  // A ref tracks the latest requested session id — if the user clicks 3D on
  // a different row before the previous fetch resolves, the stale promise
  // is ignored instead of racing the navigate call.
  const [isLoadingView3D, setIsLoadingView3D] = useState<string | null>(null);
  const latestView3DRequestRef = useRef<string | null>(null);
  // Sentinel set on unmount so any pending 3D fetch skips its navigate call —
  // clicking 3D and then leaving the page should not pull the user back into
  // the CAD viewer when the fetch resolves seconds later.
  const unmountedRef = useRef(false);
  useEffect(
    () => () => {
      unmountedRef.current = true;
      latestView3DRequestRef.current = null;
    },
    [],
  );
  const handleView3D = async (session: DRBFMSession) => {
    latestView3DRequestRef.current = session.id;
    setIsLoadingView3D(session.id);
    try {
      const results = await drbfmService.getSessionResults(session.id);
      if (unmountedRef.current || latestView3DRequestRef.current !== session.id) return;
      const anchoredCadModelId = results.failureModes?.find((fm) => fm.cadAnchor)?.cadAnchor
        ?.cadModelId;
      const params = new URLSearchParams();
      params.set('drbfm_session', session.id);
      if (anchoredCadModelId) {
        params.set('cad_model', anchoredCadModelId);
      }
      navigate(`/cad-viewer?${params.toString()}`);
    } catch {
      if (unmountedRef.current || latestView3DRequestRef.current !== session.id) return;
      // Fall back: still let the user land on the viewer; they can pick a
      // CAD model manually. The viewer surfaces its own error banner.
      navigate(`/cad-viewer?drbfm_session=${session.id}`);
    } finally {
      if (!unmountedRef.current && latestView3DRequestRef.current === session.id) {
        setIsLoadingView3D(null);
      }
    }
  };

  // Handle export session (Excel)
  const handleExportSession = async (session: DRBFMSession) => {
    setIsExporting(session.id);
    setExportError(null);

    try {
      const filename = `drbfm_${session.itemName}_${session.id.slice(0, 8)}.xlsx`;
      await drbfmService.downloadExportedExcel(session.id, filename);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('drbfm.history.exportError'));
    } finally {
      setIsExporting(null);
    }
  };

  // Handle preview session (HTML)
  const handlePreviewSession = async (session: DRBFMSession) => {
    setPreviewSession(session);
    setPreviewDialogOpen(true);
    setIsLoadingPreview(true);
    setPreviewError(null);
    setPreviewHTML('');

    try {
      const html = await drbfmService.getSessionHTMLPreview(session.id);
      setPreviewHTML(html);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('drbfm.history.previewError', 'プレビューの読み込みに失敗しました'));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Close preview dialog
  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewSession(null);
    setPreviewHTML('');
    setPreviewError(null);
  };

  // Handle convert to verification
  const handleConvertClick = async (session: DRBFMSession) => {
    setConvertSession(session);
    setConvertError(null);
    setIsLoadingResults(true);
    try {
      const results = await drbfmService.getSessionResults(session.id);
      setConvertResults(results);
      setConvertDialogOpen(true);
    } catch {
      setConvertError(t('drbfm.history.resultsFetchFailed'));
      setConvertSession(null);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (session: DRBFMSession) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteSession(sessionToDelete.id);
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    } catch (err) {
      // Error is handled by the hook
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  // Format date for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status chip color
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'analyzing':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'completed':
        return t('drbfm.history.statusCompleted', '完了');
      case 'processing':
      case 'analyzing':
        return t('drbfm.history.statusProcessing', '処理中');
      case 'failed':
        return t('drbfm.history.statusFailed', '失敗');
      case 'draft':
        return t('drbfm.history.statusDraft', '下書き');
      default:
        return status;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} size="large">
            <ArrowBackIcon />
          </IconButton>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {t('drbfm.history.title', 'DRBFM分析履歴')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchSessions()}
            disabled={isLoading}
          >
            {t('common.refresh', '更新')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/drbfm')}
          >
            {t('drbfm.history.newAnalysis', '新規分析')}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Export Error Alert */}
      {exportError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      {/* Convert Error Alert */}
      {convertError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setConvertError(null)}>
          {convertError}
        </Alert>
      )}

      {/* Sessions Table */}
      <Paper sx={{ p: 2 }}>
        {isLoading && sessions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              {t('drbfm.history.noSessions', '分析履歴がありません')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/drbfm')}
              sx={{ mt: 2 }}
            >
              {t('drbfm.history.startFirst', '最初の分析を始める')}
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('drbfm.history.columnAnalysis', '分析対象')}</TableCell>
                  <TableCell>{t('drbfm.history.columnStatus', 'ステータス')}</TableCell>
                  <TableCell>{t('drbfm.history.columnCreated', '作成日時')}</TableCell>
                  <TableCell>{t('drbfm.history.columnCompleted', '完了日時')}</TableCell>
                  <TableCell align="right">{t('drbfm.history.columnActions', '操作')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id} hover>
                    <TableCell>
                      {/* Primary label: title when present, else itemName. The
                          title input is optional and most sessions leave it
                          blank, so showing two separate columns gave us a
                          dead "-" column. Subtitle shows itemName whenever
                          title is also present, so the user keeps both. */}
                      <Typography variant="body2" fontWeight="medium">
                        {session.title?.trim() || session.itemName}
                      </Typography>
                      {session.title?.trim() && session.title.trim() !== session.itemName && (
                        <Typography variant="caption" color="text.secondary">
                          {session.itemName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(session.status)}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(session.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(session.completedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {session.status === 'completed' && (
                          <>
                            <Tooltip title={t('drbfm.history.viewResults', '結果を表示')}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewSession(session)}
                                color="primary"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('drbfm.history.view3D', '3Dで見る')}>
                              <IconButton
                                size="small"
                                onClick={() => handleView3D(session)}
                                disabled={isLoadingView3D === session.id}
                                color="info"
                              >
                                {isLoadingView3D === session.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <ThreeDIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('drbfm.history.preview', 'プレビュー')}>
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewSession(session)}
                                color="primary"
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('drbfm.history.exportExcel', 'Excel出力')}>
                              <IconButton
                                size="small"
                                onClick={() => handleExportSession(session)}
                                disabled={isExporting === session.id}
                                color="primary"
                              >
                                {isExporting === session.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('drbfm.convert.buttonLabel', '検証項目に変換')}>
                              <IconButton
                                size="small"
                                onClick={() => handleConvertClick(session)}
                                disabled={isLoadingResults}
                                color="success"
                              >
                                <ConvertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={t('drbfm.history.delete', '削除')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(session)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('drbfm.history.deleteTitle', '分析結果を削除')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('drbfm.history.deleteConfirm', '「{{itemName}}」の分析結果を削除してもよろしいですか？この操作は取り消せません。', {
              itemName: sessionToDelete?.itemName || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            {t('common.cancel', 'キャンセル')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('common.deleting', '削除中...') : t('common.delete', '削除')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HTML Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('drbfm.history.previewTitle', 'DRBFM分析結果プレビュー')}
            {previewSession && ` - ${previewSession.itemName}`}
          </Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {isLoadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t('common.loading', '読み込み中...')}</Typography>
            </Box>
          ) : previewError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{previewError}</Alert>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                '& iframe': {
                  width: '100%',
                  height: '100%',
                  border: 'none',
                },
              }}
            >
              <iframe
                srcDoc={previewHTML}
                title={t('drbfm.history.previewFrameTitle')}
                sandbox="allow-same-origin"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => previewSession && handleExportSession(previewSession)}
            disabled={isExporting === previewSession?.id || isLoadingPreview || !!previewError}
          >
            {t('drbfm.history.exportExcel', 'Excel出力')}
          </Button>
          <Button onClick={handleClosePreview}>
            {t('common.close', '閉じる')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Verification Dialog */}
      {convertSession && convertResults && (
        <ConvertToVerificationDialog
          open={convertDialogOpen}
          onClose={() => {
            setConvertDialogOpen(false);
            setConvertSession(null);
            setConvertResults(null);
          }}
          drbfmSessionId={convertSession.id}
          drbfmItemName={convertSession.itemName}
          failureModes={convertResults.failureModes || []}
          designChanges={convertResults.designChanges || []}
          countermeasures={convertResults.countermeasures || []}
        />
      )}
    </Box>
  );
};

export default DRBFMHistoryPage;
